import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import {
  center,
  clamp,
  closest,
  mediaQueryLarge,
  prefersReducedMotion,
  scheduler,
  viewTransition,
} from '@theme/utilities';

const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const DRAG_THRESHOLD = 5;

function suppressCompletedDragClick(host, pointerId) {
  const controller = new AbortController();
  const { signal } = controller;
  const doc = host.ownerDocument;
  let timer;
  const clear = () => {
    clearTimeout(timer);
    controller.abort();
  };

  doc.addEventListener('click', (event) => {
    if (event.detail === 0 || !event.composedPath().includes(host)) return;
    if ('pointerId' in event && event.pointerId !== pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clear();
  }, { capture: true, signal });
  doc.addEventListener('pointerdown', clear, { capture: true, once: true, signal });
  doc.addEventListener('keydown', clear, { capture: true, once: true, signal });
  timer = setTimeout(clear, 500);
  return clear;
}

class SlideshowViewportObserver {

  static #instance = null;

  #observer = null;

  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement =   (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  attributeChangedCallback(name, oldValue, newValue) {

    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {

        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  #connectionEpoch = 0;

  async connectedCallback() {
    super.connectedCallback();
    const connectionEpoch = ++this.#connectionEpoch;

    SlideshowViewportObserver.getInstance().observe(this);

    if (viewTransition.current) {
      await viewTransition.current;
    }
    if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

    const slideCount = this.slides?.length || 0;
    slideCount <= 1
      ? this.#setupSlideshowWithoutControls()
      : this.#setupSlideshow(connectionEpoch);
  }

  disconnectedCallback() {
    this.#connectionEpoch += 1;
    this.#disabled = false;
    this.#cancelGesture?.();
    this.#clearDragClick?.();
    this.#clearDragClick = null;
    this.refs.scroller?.style.removeProperty('scroll-snap-type');
    this.#restoreDrag = null;
    this.removeAttribute('refreshing-timeline');
    super.disconnectedCallback();

    this.suspend();

    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
      this.#scroll = undefined;
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = undefined;
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      const connectionEpoch = this.#connectionEpoch;
      const scroll = this.#scroll;
      this.#disabled = true;
      await scroll.finished;
      if (!this.#ownsScroll(connectionEpoch, scroll)) return;

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) {
        this.#disabled = false;
        return;
      }

      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) scroll.to(currentSlide, { instant: true });

      queueMicrotask(async () => {
        await scroll.finished;
        placeholder.replaceWith(targetSlide);
        if (!this.#ownsScroll(connectionEpoch, scroll)) return;
        this.#disabled = false;
        scroll.to(targetSlide, { instant: true });

        requestAnimationFrame(() => {
          if (!this.#ownsScroll(connectionEpoch, scroll)) return;
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            if (!this.#ownsScroll(connectionEpoch, scroll)) return;
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  #current = 0;

  get current() {
    return this.#current;
  }

  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => {
        if (i === value) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  #disabled = false;

  #interval = undefined;

  #scroll;

  #resizeObserver;

  #intersectionObserver = null;

  #visibleSlides = [];

  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  #setupSlideshow(connectionEpoch) {

    this.#setupIntersectionObserver();

    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    if (this.autoplay && Slideshow.isDesignMode()) this.paused = true;
    if (Slideshow.isDesignMode()) bindEditorBlockSync();

    this.resume();

    this.current = this.initialSlideIndex;

    scheduler.schedule(() => {
      if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      requestAnimationFrame(() => {
        if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;
        if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  #cancelGesture = null;

  #dragGeneration = 0;

  #clearDragClick = null;

  #restoreDrag = null;

  #ownsScroll(connectionEpoch, scroll) {
    return this.isConnected && this.#connectionEpoch === connectionEpoch && this.#scroll === scroll;
  }

  #handleMouseDown = (event) => {
    const { slides } = this;
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (!this.#scroll || !slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging || event.target.closest('model-viewer')) return;

    event.preventDefault();
    this.#cancelGesture?.();
    this.#clearDragClick?.();
    this.#clearDragClick = null;
    this.#restoreDrag?.();

    const scroll = this.#scroll;
    const connectionEpoch = this.#connectionEpoch;
    const generation = ++this.#dragGeneration;
    const { axis } = scroll;
    const startPosition = event[axis];
    const controller = new AbortController();
    const { signal } = controller;
    const doc = this.ownerDocument;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;
    let pointerId;

    const restoreSnap = () => {
      if (this.#restoreDrag === restoreSnap) this.#restoreDrag = null;
      if (!this.#ownsScroll(connectionEpoch, scroll) || this.#dragGeneration !== generation) return;
      scroll.snap = true;
      this.resume();
    };

    const endGesture = () => {
      controller.abort();
      if (this.#cancelGesture === cancel) this.#cancelGesture = null;
      this.#dragging = false;
      this.removeAttribute('dragging');

      if (pointerId !== undefined && this.hasPointerCapture(pointerId)) this.releasePointerCapture(pointerId);
    };

    const cancel = () => {
      endGesture();
      if (!moved) return;
      if (this.#ownsScroll(connectionEpoch, scroll)) restoreSnap();
      else this.refs.scroller?.style.removeProperty('scroll-snap-type');
    };
    this.#cancelGesture = cancel;

    const onPointerMove = (event) => {
      if (event.pointerType !== 'mouse' || (pointerId !== undefined && event.pointerId !== pointerId)) return;
      const current = event[axis];
      const initialDelta = startPosition - current;
      if (!moved && Math.abs(initialDelta) < DRAG_THRESHOLD) return;

      if (!moved) {
        const rtl = axis === 'x' && getComputedStyle(this.refs.scroller).direction === 'rtl';
        const towardStart = rtl ? initialDelta > 0 : initialDelta < 0;
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const nested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        if (nested && ((towardStart && this.atStart) || (!towardStart && this.atEnd))) {
          cancel();
          return;
        }

        moved = true;
        pointerId = event.pointerId;
        this.#dragging = true;
        this.setPointerCapture(pointerId);
        this.pause();
        this.setAttribute('dragging', '');
        scroll.snap = false;
      }

      event.stopImmediatePropagation();
      const delta = previous - current;
      const timeDelta = Math.max(1, performance.now() - startTime);
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);
      scroll.by(delta, { instant: true });
    };

    const onPointerUp = async (event) => {
      if (event.pointerType !== 'mouse' || (pointerId !== undefined && event.pointerId !== pointerId)) return;
      endGesture();
      if (!moved) return;

      this.#clearDragClick = suppressCompletedDragClick(this, event.pointerId);
      const { current, slides } = this;
      if (!slides?.length || !this.refs.scroller) {
        restoreSnap();
        return;
      }
      const rtl = axis === 'x' && getComputedStyle(this.refs.scroller).direction === 'rtl';
      const direction = Math.sign(velocity) * (rtl ? -1 : 1);
      const next = this.#sync();
      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);
      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      scroll.to(newSlide);
      this.#centerSelectedThumbnail(newIndex);
      this.dispatchEvent(new SlideshowSelectEvent({
        index: newIndex,
        previousIndex: currentIndex,
        userInitiated: true,
        trigger: 'drag',
        slide: newSlide,
        id: newSlide.getAttribute('slide-id'),
      }));
      if (!this.#ownsScroll(connectionEpoch, scroll) || this.#dragGeneration !== generation) return;
      this.current = newIndex;
      this.#restoreDrag = restoreSnap;
      await scroll.finished;
      restoreSnap();
    };

    doc.addEventListener('pointermove', onPointerMove, { signal });
    doc.addEventListener('pointerup', onPointerUp, { signal });
    doc.addEventListener('pointerdown', cancel, { signal });
    doc.addEventListener('pointercancel', (event) => {
      if (event.pointerType === 'mouse' && (pointerId === undefined || event.pointerId === pointerId)) cancel();
    }, { signal });
    this.addEventListener('lostpointercapture', (event) => {
      if (event.target === this && event.pointerId === pointerId) cancel();
    }, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  static isDesignMode() {
    return Boolean(
      window.Shopify?.designMode ||
        document.documentElement.classList.contains('shopify-design-mode'),
    );
  }

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide =   (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,

        rootMargin: '1px',
      }
    );

    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    if (visibleSlides.length === 0) return 0;

    scheduler.schedule(() => {

      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

let editorBlockSyncBound = false;

function bindEditorBlockSync() {
  if (editorBlockSyncBound) return;
  editorBlockSyncBound = true;

  document.addEventListener('shopify:block:select', (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;
    const slideshow = target.closest('slideshow-component');
    if (!slideshow) return;

    slideshow.suspend?.();

    const slide = target.closest('[slide-id]') ?? target.querySelector?.('[slide-id]');
    const slideId = slide?.getAttribute('slide-id');
    if (slideId) slideshow.select?.({ id: slideId }, undefined, { animate: false });
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    target.closest('slideshow-component')?.suspend?.();
  });
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
